/* eslint no-underscore-dangle: 0 */
/* eslint import/no-extraneous-dependencies: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';
import Logo from '../../../../assets/images/Template_31/Logo.png';

function HtmlTemplateThirtyOne({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [openContentUploadConfirmation, setOpenContentUploadConfirmation] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
    .collection-card {
      position: relative;
      font-family: "Montserrat", sans-serif;
      box-sizing: border-box;
      overflow: hidden;
      height: 93.6vh;
      background-color: #000;
      padding: 25px 20px;
    }
    .collection-card__header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 7px;
    }
    .collection-card__header h3 {
      color: #FFF;
      font-size: clamp(18px, 2vw, 24px);
    }
    .collection-card__header img {
      width: clamp(18px, 2vw, 25px);
      height: clamp(18px, 2vw, 25px);
    }
    .collection-card .collection-content {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      margin-block-start: clamp(30px, 6vh, 50px);
    }
    .collection-card .collection-content__title {
      color: #FFF;
      font-size: clamp(40px, 10vw, 100px);
      font-weight: 900;
      text-align: center;
      line-height: 1.1;
    }
    .collection-card .image-reflection {
      position: absolute;
      top: 20%;
      width: clamp(250px, 60%, 600px);
      max-width: 600px;
      overflow: visible;
      display: flex;
      justify-content: center;
      left: 50%;
      transform: translateX(-50%);
    }
    .collection-card .image-reflection__main {
      width: 100%;
      height: auto;
      display: block;
      object-fit: contain;
    }
    .collection-card .image-reflection::after {
      content: "";
      position: absolute;
      top: 96%;
      left: 0;
      width: 100%;
      height: 100%;
      background: var(--img-url) no-repeat center/cover;
      transform: scaleY(-1);
      opacity: 0.5;
      mask-image: linear-gradient(to bottom, rgb(0, 0, 0), transparent);
      pointer-events: none;
    }
    .collection-card__info-list {
      position: absolute;
      bottom: 5%;
      width: 100%;
      display: flex;
      justify-content: space-around;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
      padding-inline: 10px;
    }
    .collection-card__info-list--item {
      color: #FFF;
      font-size: clamp(14px, 2.5vw, 25px);
      font-weight: 700;
      text-align: center;
      margin-block-end: 10px;
      word-break: break-word;
    }
    .collection-card__info-list--offer {
      gap: 10px;
      display: flex;
      align-items: center;
      border: 2px solid #9D1617;
      flex-wrap: wrap;
    }
    .collection-card__info-list--offer span {
      display: block;
      padding: 8px 16px;
      font-size: clamp(12px, 2vw, 18px);
    }
    .collection-card__info-list--offer .offer {
      background-color: #9D1617;
    }
    @media (max-width: 1024px) {
      .collection-card .image-reflection {
        top: 25%;
      }
    }
    @media (max-width: 768px) {
      .collection-card .image-reflection {
        top: 18%;
        width: 80%;
      }
      .collection-card .collection-content__title {
        font-size: clamp(32px, 8vw, 70px);
      }
      .collection-card__info-list {
        flex-direction: column;
        bottom: 2%;
        gap: 8px;
      }
    }
    @media (max-width: 480px) {
      .collection-card {
        padding: 15px 8px;
      }
      .collection-card .collection-content__title {
        font-size: clamp(28px, 10vw, 55px);
      }
      .collection-card .image-reflection {
        width: 90%;
        top: 20%;
      }
      .collection-card__info-list {
        font-size: 14px;
        gap: 6px;
      }
    }
  `;

  const convertImage = async (url) => {
    await utils.convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertBase64 = async () => {
    toPng(htmlRef?.current, { cacheBust: true })
      .then((dataUrl) => {
        setBase64(dataUrl);
        convertImage(dataUrl);
      })
      .catch((error) => {
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertHtml = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => (setData(res)));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      /* used settimeout the page was fully rendered and then called the api so wait for the response */
      setTimeout(() => {
        convertBase64();
      }, 1000);
      convertHtml();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: <TemplateContentUpload
        base64={base64}
        setNotification={setNotification}
        fileType={fileType}
        htmlContent={htmlContent}
      />,
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: <FileFormat
        setNotification={setNotification}
        handleFileType={handleFileType}
        setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
        data={data}
      />,
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;

  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: <PopupBox
            setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
            setNotification={setNotification}
          />,
        }}
      />

      <div className="header-height" />
      <div ref={htmlRef} className="collection-card">
        <div className="collection-card__header">
          <img src={Logo} alt="Logo" />
          <h3>{contentDetails?.[0]?.header.toUpperCase()}</h3>
        </div>

        <div className="collection-content">
          {[...Array(4)].map((_, i) => (
            <h1
              key={_}
              className="collection-content__title"
              style={{
                opacity: i === 3 ? 0.3 : 1,
              }}
            >
              {contentDetails?.[0]?.item_name.toUpperCase()}
            </h1>
          ))}

          <div
            className="image-reflection"
            style={{ '--img-url': `url(${contentDetails?.[0]?.image})` }}
          >
            <img className="image-reflection__main" src={contentDetails?.[0]?.image} alt="Main" />
          </div>
        </div>

        <div className="collection-card__info">
          <ul className="collection-card__info-list">
            <li className="collection-card__info-list--item">
              {contentDetails?.[0]?.phone_number
                ?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
            </li>
            <li className="collection-card__info-list--item collection-card__info-list--offer">
              <span className="offer">{contentDetails?.[0]?.offer_percentage}</span>
              <span className="discount">DISCOUNT</span>
            </li>
            <li className="collection-card__info-list--item">{contentDetails?.[0]?.website}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateThirtyOne.propTypes = {
  templateData:
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template_title: PropTypes.string.isRequired,
    }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateThirtyOne;
